import type { Lens } from "@hookform/lenses";
import type * as React from "react";
import { useRef } from "react";
import { useController } from "react-hook-form";
import { IMaskInput } from "react-imask";

import { cn } from "tailwind-variants";

export type InchesInputProps = {
  lens: Lens<number>;
  /** Minimum allowed value in inches */
  min?: number;
  /** Maximum allowed value in inches */
  max?: number;
  /** Callback for numeric value changes (parsed number) */
  onValueChange?: (value: number) => void;
} & Omit<React.ComponentProps<"input">, "value" | "onChange" | "type" | "min" | "max">;

export function InchesInput(props: InchesInputProps) {
  const {
    lens,
    min = 0,
    max = 0,
    className,
    onValueChange,
    onFocus: externalOnFocus,
    onBlur: externalOnBlur,
    ...rest
  } = props;
  const { field } = useController(lens.interop());
  const isInFocus = useRef(false);

  const value = field.value === undefined || field.value === null ? "" : String(field.value);

  const onAccept = (val: unknown) => {
    if (!isInFocus.current) return;
    // Prevent form update while field is empty to allow user re-entry
    if (val === "" || val === null) return;

    const num = typeof val === "number" ? val : parseFloat(String(val));

    if (!Number.isNaN(num)) {
      field.onChange(num);
      onValueChange?.(num);
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    isInFocus.current = true;
    externalOnFocus?.(e);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    // Always clear focus flag when blur occurs
    isInFocus.current = false;

    // Clamp on blur to nearest allowed value
    const raw = parseFloat(e.currentTarget.value);

    if (Number.isNaN(raw)) {
      // If the user cleared the input restore the last known form value so UI reflects it
      if (field.value !== undefined && field.value !== null) {
        // Update the underlying input immediately so the user sees the restored value
        e.currentTarget.value = `${String(field.value)}"`;
      }
      field.onBlur();
      return;
    }

    let clamped = raw;
    if (min !== undefined && raw < min) clamped = min;
    if (max !== undefined && raw > max) clamped = max;

    // Always update the form value on blur, even if it's the same
    // This ensures the form knows about the latest user input
    field.onChange(clamped);
    onValueChange?.(clamped);
    field.onBlur();
    externalOnBlur?.(e);
  };

  return (
    <IMaskInput
      mask={'num"'}
      blocks={{
        num: {
          mask: Number,
          scale: 2,
          radix: ".",
          ...(min && min > 0 ? { min } : {}),
          ...(max && max > 0 ? { max } : {})
        }
      }}
      lazy={false}
      unmask={true}
      value={value}
      onAccept={onAccept}
      onFocus={onFocus}
      onBlur={onBlur}
      inputRef={field.ref}
      inputMode="decimal"
      className={cn(
        "border-input file:text-foreground placeholder:text-muted-foreground focus-visible:ring-ring flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-1 disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...rest}
    />
  );
}
